// file: src/components/modals/NewDialogModal.jsx
import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';

// Services
import { createDialog } from '../../services/dialogServices';
import { createConversation } from '../../services/conversationServices';
// Import ProjectContext to access KB data
import { useProjectContext } from '../../context/ProjectContext';
// Import specialized utility functions
import { saveDialogDetails, saveActiveSession } from '../../utils/localForageUtils';

function NewDialogModal({ onClose, onDialogCreated, projectId }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  
  // Access the pre-fetched knowledge bases from ProjectContext
  const { projects, setActiveProjectId } = useProjectContext();

  // Auto-create on mount
  useEffect(() => {
    handleCreate();
    // eslint-disable-next-line
  }, []);

  const handleCreate = async () => {
    setIsSubmitting(true);
    setErrorMsg('');

    try {
      // Extract knowledge bases from projects to avoid fetching them again
      const allKbs = [];
      
      // Collect KBs from all projects
      projects.forEach(project => {
        project.phases.forEach(phase => {
          if (phase.kb_id) {
            allKbs.push({
              id: phase.kb_id,
              project_id: project.id,
              name: `KB for ${phase.name}`
            });
          }
        });
      });
      
      console.log(`NewDialogModal: Creating dialog with projectId: ${projectId} and ${allKbs.length} pre-fetched KBs`);
      
      // 1) Create a new dialog, passing the projectId and pre-fetched KBs
      const newDialog = await createDialog('New Conversation', projectId, allKbs);
      const dialogId = newDialog.id;

      // 2) Create a conversation for the new dialog
      const newConv = await createConversation(dialogId);
      
      // 3) Cache dialog details for future reference
      await saveDialogDetails(dialogId, {
        id: dialogId,
        kb_ids: newDialog.kb_ids || [],
        project_id: projectId,
        name: 'New Conversation'
      });
      
      // 4) Save as active session
      await saveActiveSession(dialogId, newConv.id);

      // 5) Set the active project
      if (projectId) {
        setActiveProjectId(projectId);
      }

      // 6) Tell parent the new IDs, then close
      onDialogCreated?.({
        dialogId,
        conversationId: newConv.id,
      });
      onClose();
    } catch (err) {
      console.error('NewDialogModal error:', err);
      setErrorMsg('Failed to create a new conversation. Please try again.');
      setIsSubmitting(false);
    }
  };

  // If no error occurred, we actually hide the modal quickly.
  // But if there's an error, we show it in a small overlay.
  if (!errorMsg) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="w-full max-w-md p-6 rounded-lg bg-white dark:bg-card/95 shadow-xl border border-gray-400 dark:border-border/50">
        <div className="mb-4">
          <div className="flex items-center mb-3">
            <div className="size-8 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center mr-3">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Error Creating Conversation</h2>
          </div>
          <p className="text-sm text-gray-700 dark:text-muted-foreground">
            {errorMsg}
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center disabled:opacity-50"
          >
            <X className="h-4 w-4 mr-2" />
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewDialogModal;

